/* Storefront form repair. The original licensed theme and payment loaders are retained. */
(() => {
  'use strict';
  if (window.SolvirosolCartRepair) return;
  const api = window.SolvirosolCartRepair = { pending: false, uncertain: false, allowDependent: false, queued: new Set() };
  const digits = value => /^\d+$/.test(String(value || '')) && Number(value) > 0;
  const root = () => window.Shopify?.routes?.root || '/';
  const cartURL = () => window.routes?.cart_url || root() + 'cart';
  const goToCart = () => window.location.assign(cartURL());
  const integer = (value, label) => {
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < 1) throw new Error(label + ' must be a positive whole number.');
    return number;
  };
  function showError(component, message, checkCart = false) {
    let wrapper = component.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper';
      wrapper.setAttribute('role', 'alert');
      component.prepend(wrapper);
    }
    wrapper.replaceChildren();
    const text = document.createElement('span');
    text.className = 'product-form__error-message';
    text.textContent = message;
    wrapper.append(text);
    if (checkCart) {
      const link = document.createElement('a');
      link.href = cartURL();
      link.textContent = 'View your cart before trying again';
      link.className = 'link solvirosol-cart-check-link';
      wrapper.append(document.createTextNode(' '), link);
    }
    wrapper.hidden = false;
    component.dispatchEvent(new CustomEvent('solvirosol:cart-error', { bubbles: true, detail: { message, checkCart } }));
  }
  function productData(component) {
    try { return JSON.parse(component.querySelector('[data-solvirosol-product]')?.textContent || 'null'); }
    catch { throw new Error('Product selection data could not be read. Please refresh this page.'); }
  }
  function selectedVariant(component, form, data) {
    const input = form.querySelector('[name="id"]');
    let id = input?.value;
    // Scope selection to this form's section, including dynamically inserted quick-add forms.
    const info = component.productInfo || component.closest('[id^="MainProduct-"], .shopify-section');
    const picker = info?.querySelector('variant-selects, variant-radios');
    if (data && picker && picker.dataset.section === component.dataset.section) {
      const groups = [...picker.querySelectorAll('.product-form__input')];
      const options = groups.map(group => group.querySelector('select')?.value || group.querySelector('input:checked')?.value);
      if (options.length && options.every(Boolean)) {
        const match = data.variants.find(variant => variant.options.length === options.length && variant.options.every((value, i) => String(value) === options[i]));
        if (!match) throw new Error('This combination is unavailable. Please choose another variant.');
        id = String(match.id);
        if (input) input.value = id;
      }
    }
    if (!digits(id)) throw new Error('Please select a product variant before adding it to your cart.');
    if (data && !data.variants.some(variant => String(variant.id) === String(id))) {
      throw new Error('The selected variant does not belong to this product. Please refresh and select it again.');
    }
    return String(id);
  }
  function prepare(component, form) {
    const data = productData(component);
    if (data && !Array.isArray(data.variants)) throw new Error('Product variants are unavailable. Please refresh this page.');
    const formData = new FormData(form);
    const section = component.dataset.section;
    const scope = component.productInfo || component.closest('[id^="ProductInfo-"], [id^="MainProduct-"]') || component;
    const belongsToForm = input => {
      if (input.disabled || input.matches(':disabled') || ((input.type === 'radio' || input.type === 'checkbox') && !input.checked)) return false;
      if (input.form && input.form !== form) return false;
      const owner = input.closest('product-form');
      if (owner && owner !== component) return false;
      const addOn = input.closest('product-info-upsell, cart-drawer-upsell, cart-drawer-gift');
      return !addOn || addOn.contains(component);
    };
    const lookup = prefix => section ? document.getElementById(prefix + section) : null;
    const bundle = lookup('bundle-deals-') || component.bundleDeals;
    const quantityBreaks = lookup('quantity-breaks-') || component.quantityBreaks;
    const quantityInput = [...form.elements].find(input => input.name === 'quantity' && !input.disabled);
    const properties = [];
    for (const [name, value] of formData.entries()) {
      const match = /^properties\[(.+)\]$/.exec(name);
      if (match) properties.push([match[1], value]);
    }
    // App widgets may sit beside the form without an HTML form attribute.
    // Read only this product's controls, excluding separate upsell/product forms.
    scope.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]').forEach(input => {
      if (input.form === form || !belongsToForm(input)) return;
      const match = /^properties\[(.+)\]$/.exec(input.name);
      if (!match) return;
      if (input.type === 'file') [...input.files].forEach(file => properties.push([match[1], file]));
      else properties.push([match[1], input.value]);
    });
    (component.customFields || []).forEach(field => {
      if (field.dataset.disabled !== 'true' && field.fieldName) properties.push([field.fieldName, typeof field.prevValue === 'object' ? JSON.stringify(field.prevValue) : field.prevValue ?? '']);
    });
    const planInput = [...component.querySelectorAll('.appstle-active-option [name="selling_plan"]')].find(belongsToForm) ||
      [...form.elements].find(input => input.name === 'selling_plan' && belongsToForm(input)) ||
      [...scope.querySelectorAll('.appstle-active-option [name="selling_plan"], .sls-purchase [name="selling_plan"], skio-plan-picker [name="selling_plan"]')].find(belongsToForm);
    const plan = planInput?.value || '';
    if (plan && !digits(plan)) throw new Error('Please select a valid subscription option.');
    const items = [];
    const push = (id, quantity, main = false, sellingPlan = '', sourceIndex = null) => {
      if (!digits(id)) throw new Error('A bundle, gift or add-on has no valid variant. Please review your selections.');
      const item = { id: String(id), quantity: integer(quantity, 'Quantity'), main, plan: String(sellingPlan || ''), properties: main ? properties : [], sourceIndex };
      if (item.plan && !digits(item.plan)) throw new Error('A subscription option is invalid. Please review your selections.');
      const known = main && data?.variants.find(variant => String(variant.id) === item.id);
      if (known?.available === false) throw new Error('This variant is currently unavailable in your market. Please choose another variant.');
      const same = sourceIndex === null && items.find(other => other.id === item.id && other.plan === item.plan && other.main === item.main);
      if (same) same.quantity += item.quantity;
      else items.push(item);
    };
    if (component.ref) {
      // Some bundle apps own indexed inputs. Keep them, but validate instead of silently dropping them.
      const indexed = new Map();
      for (const [name, value] of formData.entries()) {
        const match = /^items\[(\d+)\]\[(id|quantity|selling_plan)\]$/.exec(name);
        if (match) { const item = indexed.get(match[1]) || {}; item[match[2]] = value; indexed.set(match[1], item); }
      }
      if (!indexed.size) throw new Error('Your bundle is empty. Please select its items again.');
      indexed.forEach((item, index) => push(item.id, item.quantity ?? 1, false, item.selling_plan, index));
    } else if (bundle) {
      if (!Array.isArray(bundle.formVariants) || !bundle.formVariants.length) throw new Error('Please select at least one bundle item.');
      bundle.formVariants.forEach(item => push(item.id, item.quantity ?? 1, false, item.selling_plan));
    } else {
      const mainBundle = component.isMain || component.classList.contains('main-product-form')
        ? [...document.querySelectorAll('product-info-upsell')].filter(item => item.classList.contains('main-offer-item-' + section)) : [];
      if (mainBundle.length) {
        mainBundle.forEach(item => push(item.dataset.id, item.dataset.quantity || 1, item.dataset.isMainPdpProduct !== 'false', plan));
      } else if (Array.isArray(quantityBreaks?.formVariants) && quantityBreaks.formVariants.length) {
        quantityBreaks.formVariants.forEach(item => {
          const id = typeof item === 'object' ? item.id : item;
          if (data && !data.variants.some(variant => String(variant.id) === String(id))) throw new Error('A quantity-break selection is stale. Please choose its variants again.');
          push(id, typeof item === 'object' ? item.quantity ?? 1 : 1, true, plan);
        });
      } else {
        const id = selectedVariant(component, form, data);
        const quantity = quantityBreaks?.selectedQuantity ?? quantityInput?.value ?? 1;
        if (quantityInput && !quantityInput.checkValidity()) throw new Error('Please enter a quantity within the displayed limits.');
        push(id, quantity, true, plan);
      }
    }
    if (!component.ref) {
      const appendSelected = node => {
        if (node.dataset.selected !== 'true') return;
        push(node.dataset.id, node.dataset.quantity || 1, false, node.querySelector('[name="selling_plan"]')?.value);
      };
      if (!component.isCartUpsell && !component.skipCart) {
        const drawer = document.querySelector('cart-drawer');
        drawer?.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift').forEach(node => {
          const exists = [...drawer.querySelectorAll('.cart-item')].some(item => item.classList.contains('cart-item--product-' + node.dataset.handle));
          if (!exists) appendSelected(node);
        });
      }
      if (!bundle || component.dataset.includePdpUpsells === 'true') {
        const upsells = section ? [...document.querySelectorAll('product-info-upsell')].filter(node => node.classList.contains('product-info-upsell-' + section)) : [];
        upsells.filter(node => ![...(component.mainBundleItems || [])].includes(node)).forEach(appendSelected);
      }
      const gifts = lookup('quantity-gifts-') || component.quantityGifts;
      (gifts?.unlockedItems || []).forEach(id => { if (!items.some(item => item.id === String(id))) push(id, 1); });
      quantityBreaks?.querySelectorAll('quantity-break-upsells').forEach(node => {
        (node.unlockedItems || []).forEach(id => { if (!items.some(item => item.id === String(id))) push(id, 1); });
      });
    }
    if (!items.length) throw new Error('Please select a product before adding it to your cart.');
    for (const item of items) {
      const known = item.main && data?.variants.find(variant => String(variant.id) === item.id);
      if (known) {
        const rule = known.quantity_rule || {};
        const minimum = Number(rule.min) || 1, increment = Number(rule.increment) || 1;
        if (item.quantity < minimum || (rule.max && item.quantity > Number(rule.max)) || item.quantity % increment !== 0) {
          throw new Error('Please follow the quantity rule for this variant (minimum ' + minimum + ', increments of ' + increment + (rule.max ? ', maximum ' + rule.max : '') + ').');
        }
      }
    }
    // Start fresh: stale generated items never survive a second submission or variant switch.
    const body = new FormData();
    for (const [name, value] of formData.entries()) {
      if (/^(?:items(?:\[|$)|id$|quantity$|selling_plan$|properties\[|sections$|sections_url$|return_to$)/.test(name)) continue;
      body.append(name, value);
    }
    items.forEach((item, index) => {
      body.append('items[' + index + '][id]', item.id);
      body.append('items[' + index + '][quantity]', String(item.quantity));
      if (item.plan) body.append('items[' + index + '][selling_plan]', item.plan);
      const itemProperties = component.ref ? [...formData.entries()].filter(([name]) => name.startsWith('items[' + item.sourceIndex + '][properties][')) : [];
      itemProperties.forEach(([name, value]) => body.append(name.replace('items[' + item.sourceIndex + ']', 'items[' + index + ']'), value));
      item.properties.forEach(([name, value]) => body.set('items[' + index + '][properties][' + name + ']', value));
    });
    return { body, items };
  }
  function busy(component, form, event) {
    const buttons = new Set([...form.elements].filter(element => element.tagName === 'BUTTON' && element.type === 'submit'));
    if (component.submitButton) buttons.add(component.submitButton);
    const extra = event?.currentTarget;
    if (extra?.tagName === 'BUTTON') buttons.add(extra);
    const states = [...buttons].map(button => ({ button, disabled: button.disabled, aria: button.getAttribute('aria-disabled') }));
    states.forEach(({button}) => { button.disabled = true; button.setAttribute('aria-disabled', 'true'); button.classList.add('loading'); });
    return () => states.forEach(({button, disabled, aria}) => {
      const currentId = form.querySelector('[name="id"]')?.value;
      let unavailable = false;
      try { unavailable = productData(component)?.variants?.some(variant => String(variant.id) === currentId && variant.available === false) || false; } catch { /* a replaced form will validate its own metadata on its next submission */ }
      button.disabled = disabled || unavailable;
      if (aria === null) button.removeAttribute('aria-disabled'); else button.setAttribute('aria-disabled', aria);
      button.classList.remove('loading');
    });
  }
  async function submit(component, event) {
    event?.preventDefault();
    if (api.pending || component.dataset.cartPending === 'true') {
      if (api.allowDependent && component.isCartUpsell === true && component.dataset.cartPending !== 'true') api.queued.add(component);
      return;
    }
    if (api.uncertain) { showError(component, 'The last request could not be confirmed. Check your cart before adding again.', true); return; }
    const form = component.querySelector('form');
    if (!form) return;
    const trigger = event?.submitter || event?.currentTarget;
    const skipRequested = component.skipCart || form.querySelector('[data-skip-cart="true"]') || trigger?.dataset.skipCartButton === 'true';
    if (event?.submitter?.disabled || component.submitButton?.disabled) return;
    const button = component.submitButton;
    if (button && Number(button.dataset.requiredFields || 0) > Number(button.dataset.validFields || 0)) {
      showError(component, 'Please complete the required product fields.'); return;
    }
    let payload;
    try { payload = prepare(component, form); }
    catch (error) { showError(component, error.message); return; }
    api.pending = true;
    component.dataset.cartPending = 'true';
    const restore = busy(component, form, event);
    const spinner = component.querySelector('.loading-overlay__spinner');
    spinner?.classList.remove('hidden');
    component.querySelector('.product-form__error-message-wrapper')?.setAttribute('hidden', '');
    const cart = document.querySelector('cart-drawer, cart-notification');
    let sections = [];
    try { sections = cart?.getSectionsToRender?.().map(section => section.id) || []; } catch { /* addition remains independent of drawer markup */ }
    if (sections.length) { payload.body.append('sections', sections.join(',')); payload.body.append('sections_url', window.location.pathname); }
    let added = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(root() + 'cart/add.js', { method: 'POST', body: payload.body, credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, signal: controller.signal });
      let result;
      try { result = await response.json(); } catch { api.uncertain = true; throw new Error('The server response could not be confirmed.'); }
      if (!response.ok || result.status) {
        if (response.status >= 500) api.uncertain = true;
        const detail = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'Your cart could not be updated (HTTP ' + response.status + ').';
        throw new Error(detail);
      }
      if (!digits(result.id) && !(Array.isArray(result.items) && result.items.length && result.items.every(item => digits(item.id)))) {
        api.uncertain = true;
        throw new Error('The server did not confirm which variants were added.');
      }
      added = true;
      // Once Shopify confirms an addition, all recovery is navigation/GET only.
      if (skipRequested) {
        window.location.assign(root() + 'checkout'); return;
      }
      if (component.classList.contains('main-product-form') && component.closest('section[id^="MainProduct-"]') && !component.closest('quick-add-modal')) {
        goToCart(); return;
      }
      if (!cart || !sections.length) { goToCart(); return; }
      if (!result.sections || sections.some(id => typeof result.sections[id] !== 'string')) {
        const refresh = await fetch(cartURL() + '?sections=' + encodeURIComponent(sections.join(',')), { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!refresh.ok) throw new Error('Cart sections could not be refreshed.');
        result.sections = await refresh.json();
        if (sections.some(id => typeof result.sections[id] !== 'string')) throw new Error('A cart section is unavailable.');
      }
      const modal = component.closest('quick-add-modal');
      if (modal?.hide) modal.hide(true);
      cart.setActiveElement?.(document.activeElement);
      api.allowDependent = true;
      cart.renderContents(result, component.isCartUpsell === true);
      api.allowDependent = false;
      if (!component.isCartUpsell) cart.open?.();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: payload.items[0].id, cartData: result });
      }
      component.dispatchEvent(new CustomEvent('solvirosol:cart-added', { bubbles: true, detail: { variantIds: payload.items.map(item => item.id) } }));
    } catch (error) {
      if (added) { api.queued.clear(); goToCart(); return; }
      if (error.name === 'AbortError' || error instanceof TypeError) api.uncertain = true;
      const message = api.uncertain ? error.message + ' Your request may have reached Shopify. Please check your cart before trying again.' : error.message;
      showError(component, message, api.uncertain);
    } finally {
      clearTimeout(timeout);
      api.pending = false;
      api.allowDependent = false;
      delete component.dataset.cartPending;
      restore();
      spinner?.classList.add('hidden');
      if (added && !api.uncertain && api.queued.size) {
        const [next] = api.queued;
        api.queued.delete(next);
        queueMicrotask(() => { if (next.isConnected) submit(next); });
      } else if (!added) api.queued.clear();
    }
  }
  // Bound legacy listeners are stopped at capture; programmatic sticky/bundle calls use the same method.
  document.addEventListener('submit', event => {
    const component = event.target.closest('product-form');
    if (!component || !customElements.get('product-form')) return;
    const submitter = event.submitter;
    if (submitter && submitter.name !== 'add' && !submitter.matches('.product-form__submit, .atc-button, .main-product-atc')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    submit(component, event);
  }, true);
  document.addEventListener('click', event => {
    const button = event.target.closest('[id^="SectionAtcBtn-"]');
    if (!button || button.disabled || !customElements.get('product-form')) return;
    const component = document.querySelector('.main-product-form');
    if (!component) return;
    event.preventDefault(); event.stopImmediatePropagation();
    submit(component, { currentTarget: button });
  }, true);
  customElements.whenDefined('product-form').then(() => {
    const prototype = customElements.get('product-form').prototype;
    prototype.handleSubmit = function(event) { return submit(this, event); };
  });
})();
